import {
  faFile,
  faFileExcel,
  faFileWord,
  faFilePdf,
  faFilePowerpoint,
  faFileImage,
  faFileCsv,
} from "@fortawesome/free-solid-svg-icons";
import {
  PopupDismissal,
  UserAssignmentRole,
  UserAssignmentRoleDisplayedNames,
  UserInfo,
} from "../store/auth/types";
import {
  InviteStatuses,
  InviteStatusesDisplayedNames,
  Measurement,
  MeasurementType,
  Student,
  StudentInfo,
  TeacherInfo,
} from "../store/onboarding/types";

export const getFullName = (
  userInfo: UserInfo | StudentInfo | Student | undefined
) => (userInfo ? `${userInfo.first_name} ${userInfo.last_name}` : "");

export const getFirstName = (
  userInfo: UserInfo | StudentInfo | Student | undefined
) => (userInfo ? `${userInfo.first_name}` : "");

export const getLoggedBy = (
  user: number | UserInfo | undefined,
  myId?: number
) => {
  if (user !== undefined) {
    if (typeof user === "number") {
      return user === myId ? "Me" : "Other User";
    } else {
      return getFullName(user);
    }
  }
  return "Other User";
};

export const getInviteStatusDisplay = (teacher: TeacherInfo): string => {
  if (teacher?.invite) {
    if(teacher.invite.is_expired && (teacher.invite.status == InviteStatuses.INVITE_SENT)) {
      return InviteStatusesDisplayedNames[InviteStatuses.INVITE_EXPIRED as InviteStatuses];
    } else {
      return InviteStatusesDisplayedNames[teacher.invite.status];
    }
  } else {
    return "";
  }
};

export const getUserAssigmentRoleDisplayedName = (
  role?: UserAssignmentRole
) => {
  return role ? UserAssignmentRoleDisplayedNames[role] : "";
};

export const getTitleByMeasurement = (
  value: string | number,
  measurement?: Measurement
) => {
  if (!measurement || measurement.type !== MeasurementType.CATEGORY) {
    return value;
  }
  return (
    measurement.possible_values?.find((pv) => pv.order == value)
      ?.display_name ?? value
  );
};

export const generateInterventionAbbrev = (name: string) => {
  return name
    .split(" ")
    .map((word: string) => (word.startsWith("(") ? null : word[0]))
    .reduce((pV, cV) => {
      return cV ? pV + cV : pV;
    }, "");
};

export const generateAbbrev = (word: string) => {
  return word.match(/\b([A-z/-])|([A-Z])+/gm)?.join("");
};

export const isShowConfimationDialog = (userDismissTimeStamp: string | undefined) => {
  if(userDismissTimeStamp) {
    const popupDismissTime = new Date(userDismissTimeStamp).getTime();
    const now = Date.now();
    return popupDismissTime <= now;
  } else {
    return true;
  }
};

export const escapeSpecialCharacter = (value: any) => {
  return value.replace(/[^a-zA-Z0-9\s'-]/g, "");
}

export const allowAlphaNumbers = (value: any) => {
  return value.replace(/[^a-zA-Z0-9]/g, "");
};

export const allowNumbers = (value: any) => {
  return value.replace(/[^0-9]/g, "");
};

export const splitStudentName = (
  userInfo: UserInfo | StudentInfo | Student | undefined
) =>  {
  const fullName = userInfo ? `${userInfo.first_name} ${userInfo.last_name}` : ""
  if (!fullName) return { first_name: "", last_name: "" };

  // Case 2: Two words with comma (e.g. "Smith, John")
  if (fullName.includes(",") && fullName.split(",").length === 2) {
    const [last, first] = fullName.split(",").map(s => s.trim());
    return { first_name: first, last_name: last };
  }
  
  // Case 1: Three words (e.g. "John Michael Smith")
  const words = fullName.trim().split(/\s+/);
  if (words.length > 3) {
    return { first_name: `${words[0]} ${words[1]}`, last_name: words[2] };
  }

  // Case 3: Two words with space (e.g. "John Smith")
  if (words.length === 2) {
    return { first_name: words[0], last_name: words[1] };
  }

  // Default: If only one word or other cases
  return { first_name: fullName, last_name: "" };
}

export const getFileIcon = (
  fileName?: string,
  mimeType?: string
) => {
  const extension =
    fileName?.split(".").pop()?.toLowerCase() || "";

  switch (extension) {
    case "doc":
    case "docx":
      return faFileWord;

    case "xls":
    case "xlsx":
      return faFileExcel;

    case "csv":
      return faFileCsv;

    case "ppt":
    case "pptx":
    case "pps":
      return faFilePowerpoint;

    case "pdf":
      return faFilePdf;

    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
      return faFileImage;

    default:
      return faFile;
  }
};

export const uniqueByKey = (array: any[], key: any = 'id') => {
  return Array.from(new Map(array.map((item: any) => [item[key], item])).values());
};
